import { CircularProgress, Grid, InputAdornment, TextField } from '@mui/material';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useNavigate } from 'react-router-dom';
import { MuiTextFieldWithFocus } from '../../../config/MaterialUIConstants';
import { useInfiniteAppointmentList } from '../../../services/business/appointment';
import SearchTextfeild from '../../../utility/Svg/SearchTextfeild';
import useDebounce from '../../../utility/useDebounce';
import TabCards from './TabCards';

const AppointmentTab = ({ customerId, getAppointmentDetails, vehicleId }) => {
    const navigate = useNavigate();
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 300);

    const queryParams = {
        limit: 20,
        customerId: customerId,
        customerSearch: debouncedSearch,
    };

    if (vehicleId) {
        queryParams.vehicleId = vehicleId;
    }

    const {
        data: appointmentQueryData,
        fetchNextPage,
        hasNextPage,
        isFetching,
        isFetchingNextPage,
        isLoading,
    } = useInfiniteAppointmentList(queryParams, {
        enabled: !!customerId,
        refetchOnMount: true,
    });

    // Flatten all pages into a single array
    const appointmentList = useMemo(() => {
        if (!appointmentQueryData?.pages) return [];
        return appointmentQueryData.pages.flatMap((page) => {
            // Handle different API response structures
            if (Array.isArray(page)) {
                return page;
            }
            return page?.data || [];
        });
    }, [appointmentQueryData]);

    const handleLoadMore = () => {
        if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    };

    return (
        <Grid container spacing={3}>
            {/* Searchbar  */}
            <Grid item xs={12}>
                <TextField
                    fullWidth
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search appointment, licence plate or unit number"
                    sx={{ ...MuiTextFieldWithFocus, background: '#fff' }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchTextfeild />
                            </InputAdornment>
                        ),
                    }}
                />
            </Grid>

            {isLoading && appointmentList.length === 0 && (
                <Grid item xs={12} container justifyContent="center" alignItems="center" style={{ height: 350 }}>
                    <CircularProgress />
                </Grid>
            )}

            {!isLoading && appointmentList.length === 0 && (
                <Grid item xs={12} container justifyContent="center" alignItems="center" style={{ height: 350 }}>
                    No Data Found
                </Grid>
            )}

            {appointmentList.length > 0 && (
                <Grid item xs={12} container>
                    <InfiniteScroll
                        dataLength={appointmentList.length}
                        next={handleLoadMore}
                        hasMore={hasNextPage || false}
                        loader={
                            isFetchingNextPage && (
                                <Grid item xs={12} justifyContent="center" alignItems="center" style={{ padding: '20px 0' }}>
                                    <CircularProgress size={24} />
                                </Grid>
                            )
                        }
                        endMessage={
                            !hasNextPage &&
                            appointmentList.length > 0 && (
                                <Grid item xs={12} justifyContent="center" alignItems="center" style={{ padding: '20px 0' }}>
                                    {/* <p style={{ color: '#666', fontSize: '14px', margin: 0 }}>No more appointments to load</p> */}
                                </Grid>
                            )
                        }
                    >
                        <Grid container spacing={3}>
                            {appointmentList.map((item) => {
                                const {
                                    _id,
                                    Total,
                                    dropOffDateTime,
                                    appointmentNumber,
                                    status,
                                    isEmailSent,
                                    vehicle,
                                    note,
                                    noteForCustomer,
                                    serviceCount,
                                    services,
                                } = item;

                                const vehicleString =
                                    `${vehicle?.year || ''} ${vehicle?.vehicleMake?.name || ''} ${vehicle?.vehicleModel?.name || ''} ${
                                        vehicle?.vehicleModelTrim?.name || ''
                                    }`.trim() || '';

                                const province = `${vehicle?.provinceCode || ''} ${vehicle?.licenseNumber || ''}`.trim() || '';

                                const fleetUnitNumber = vehicle?.fleetUnitNumber || '';

                                const vin = vehicle?.vin || '';

                                return (
                                    <Grid item xs={12} key={_id}>
                                        <TabCards
                                            appointmentCard
                                            amount={0}
                                            invoiceNumber={appointmentNumber}
                                            date={dayjs(dropOffDateTime).format('MMM DD, YYYY')}
                                            vehicle={vehicleString}
                                            province={province}
                                            vin={vin}
                                            fleetUnitNumber={fleetUnitNumber}
                                            totalService={
                                                serviceCount > 1
                                                    ? `${serviceCount} Services`
                                                    : `${(services?.length > 0 && (services[0]?.name || services[0]?.serviceName)) || undefined}`
                                            }
                                            navigate={() => {
                                                item?.status == 'customer_paid' || item?.status == 'mark_as_no_show'
                                                    ? navigate(`/appointment/past-appointment/${item?._id}`)
                                                    : getAppointmentDetails(item?._id);
                                            }}
                                            navigateUrl={''}
                                            note={noteForCustomer}
                                        />
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </InfiniteScroll>
                </Grid>
            )}
        </Grid>
    );
};

export default AppointmentTab;
